import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Avatar, Button } from 'antd';
import { GlobalOutlined, UserOutlined } from '@ant-design/icons';
import HeaderDropdown from '../../HeaderDropdown/HeaderDropdown';

export const StyledDropdownContainer = styled.div`
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  transition: all 0.3s;

  .ant-btn-link {
    height: fit-content;
  }
`;

export const StyledFullNameSpan = styled.span`
  color: var(--textColor);
  font-size: 14px;
  font-weight: 600;
`;

export const StyledRoleP = styled.span`
  font-size: 12px;
  color: var(--dimGray);

  &::before {
    content: '·';
    margin: 0 6px;
  }
`;

const StyledPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 40px;
  min-width: 40px;
  padding: ${({ $round }) => ($round ? '0' : '0 16px 0 6px')};
  background: var(--ds-surface);
  border-radius: var(--ds-radius-pill);
  box-shadow: var(--ds-shadow-card);

  .ant-btn-link {
    display: flex;
    align-items: center;
    height: fit-content;
    padding: 0;
  }
`;

const StyledAvatar = styled(Avatar)`
  background: var(--pear);
  color: var(--darkGray);
  font-size: 13px;
  font-weight: 700;
`;

/**
 * HeaderUserAndLanguageDropdowns component.
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.profileMenu - The profile menu object.
 * @param {Object} props.localizationMenu - The localization menu object.
 * @param {Object} props.user - The user object.
 * @param {Function} props.t - The translation function.
 * @param {Element} props.StyledDropdownContainerComponent - The styled dropdown container component.
 * @returns {JSX.Element} - The HeaderUserAndLanguageDropdowns component.
 */
const HeaderUserAndLanguageDropdowns = ({
  profileMenu,
  localizationMenu,
  user,
  t,
  StyledDropdownContainerComponent
}) => (
  <>
    <HeaderDropdown overlayMenu={profileMenu}>
      {globalThis.innerWidth <= 992 ? (
        <StyledDropdownContainerComponent>
          <StyledPill $round>
            <UserOutlined />
          </StyledPill>
        </StyledDropdownContainerComponent>
      ) : (
        <StyledDropdownContainerComponent>
          <StyledPill>
            <StyledAvatar
              size={36}
              icon={user?.photo ? '' : <UserOutlined />}
              src={user?.photo ? user.photo : ''}
            >
              {`${user?.first_name[0]}${user?.last_name[0]}`}
            </StyledAvatar>
            <Button type="link">
              <StyledFullNameSpan>
                {`${user?.first_name} ${user?.last_name}`}
              </StyledFullNameSpan>
              <StyledRoleP>
                {t(`users.tags.${user?.role?.split(':')[1]}`)}
              </StyledRoleP>
            </Button>
          </StyledPill>
        </StyledDropdownContainerComponent>
      )}
    </HeaderDropdown>
    <HeaderDropdown overlayMenu={localizationMenu}>
      <StyledDropdownContainerComponent>
        <StyledPill $round>
          <GlobalOutlined
            style={{
              fontSize: 16,
              color: 'var(--dimGray)'
            }}
          />
        </StyledPill>
      </StyledDropdownContainerComponent>
    </HeaderDropdown>
  </>
);

HeaderUserAndLanguageDropdowns.propTypes = {
  profileMenu: PropTypes.shape({
    onClick: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  localizationMenu: PropTypes.shape({
    onClick: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    photo: PropTypes.string,
    role: PropTypes.string
  }),
  t: PropTypes.func.isRequired,
  StyledDropdownContainerComponent: PropTypes.elementType.isRequired
};

HeaderUserAndLanguageDropdowns.defaultProps = {
  user: null
};

export default HeaderUserAndLanguageDropdowns;
